import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Controller } from 'react-hook-form';
import {
  DNABox,
  DNAButton,
  DNAContextMenu,
  DNAText,
  Iffy,
  TextField,
} from '@alucio/lux-ui/src';
import { Input } from '@ui-kitten/components';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { CommonIcon, DNAPopover } from 'src/components/DNA';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { useHubRHFromState } from 'src/screens/Hubs/EditHub/useHubRHFormStateProvider';
import { HubORM } from 'src/types/orms';
import TenantLogo from 'src/components/DNA/Header/TenantLogo';
import NetworkConnectionBanner from 'src/components/NetworkConnection/NetworkConnectionBanner';
import { HubType } from '@alucio/aws-beacon-amplify/lib/models';
import { HubVariant } from 'src/state/context/Hubs/HubsStateProvider';
import DNACommonConfirmation from 'src/components/DNA/Modal/DNACommonConfirmation';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { useDispatch } from 'src/state/redux';

type HeaderProps = {
  hubORM: HubORM
}

const styles = StyleSheet.create({
  breadcrumbHeader: {
    height: 48,
    backgroundColor: colors['color-text-white'],
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
    paddingHorizontal: 24,
  },
  chevronIcon: {
    height: 18,
    color: colors['color-text-tertiary'],
    marginHorizontal: 15,
    top: 5,
  },
  hubTitleHeader: {
    height: 64,
    backgroundColor: colors['color-text-white'],
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
    paddingHorizontal: 24,
  },
  hubTitleContainer: {
    paddingHorizontal: 30,
  },
  hubTitle: {
    marginHorizontal: 8,
  },
  editHubTitleInputBox: {
    width: 500,
  },
  contextMenuItems: {
    minWidth: 160,
    right: 66,
  },
});

const Header: React.FC<HeaderProps> = ({ hubORM }) => {
  const {
    handleCancelEdit,
    handleSaveNCallback,
    handleDeleteHub,
    errorMessages,
    handleFormInvalid,
    openFormInvalidWarningModal,
    openPreviewWindow,
    handlePressShare,
    variant,
  } = useHubsState()
  const { rhForm, isSaving, setIsSaving } = useHubRHFromState()
  const { isOnline } = useAppSettings()
  const dispatch = useDispatch()
  const [editNameMode, setEditNameMode] = useState<boolean>(false)
  const [hubName, setHubName] = useState<string>(hubORM.model.name || '')
  const [previousHubName, setPreviousHubName] = useState<string>(hubORM.model.name || '')
  const formattedFormErrors = useMemo(() => [...new Set(errorMessages)].join('\n'), [errorMessages])
  const nameRef = useRef<Input>(null)

  useEffect(() => {
    if (editNameMode) nameRef.current?.focus()
  }, [editNameMode])

  useEffect(() => {
    if (isSaving) {
      const widgetFormValue = getValues('name')
      setEditNameMode(false)
      setHubName(widgetFormValue)
      setPreviousHubName(widgetFormValue)
    }
  }, [isSaving])
  
  const {
    handleSubmit,
    setValue,
    getValues,
    trigger,
    control,
    formState: { isDirty, dirtyFields },
  } = rhForm

  const cancelHubNameChange = useCallback(() => {
    setEditNameMode(false)
    setHubName(previousHubName)
    setValue('name', previousHubName)
    trigger(`name`)
  }, [setValue, trigger, previousHubName])

  const onSubmitHubName = useCallback(() => {
    const widgetFormValue = getValues('name')
    if (!widgetFormValue) cancelHubNameChange()
    else {
      setHubName(widgetFormValue)
      setPreviousHubName(widgetFormValue)
      setEditNameMode(false)
    }
  }, [getValues, cancelHubNameChange])

  const cancelEditHub = useCallback(() => {
    handleCancelEdit(!!Object.keys(dirtyFields).length && isDirty)
  }, [rhForm, isDirty, dirtyFields])

  const onSaveNShare = useCallback((data: any) => {
    setIsSaving(true)
    handlePressShare(hubORM?.model.id, data, !!Object.keys(dirtyFields).length && isDirty, hubORM)
    setTimeout(() => {
      setIsSaving(false)
    }, 1000)
  }, [rhForm, isDirty, dirtyFields, hubORM])

  const onSaveNPreview = useCallback((data: any) => {
    setIsSaving(true)

    const previewCallback = () => {
      openPreviewWindow(hubORM?.model.id)
      rhForm.reset({}, { keepValues: true })
    }

    // we check the readPermissions because we only show the modal if the template has been shared at least once
    const shouldShowModal = variant === HubVariant.TEMPLATE && 
      !!Object.keys(dirtyFields).length && isDirty && 
      (hubORM?.model.readPermissions ?? []).length > 0
    if (shouldShowModal) {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: false,
        component: () => (<DNACommonConfirmation
          cancelText="Cancel"
          confirmActionText="Save and continue"
          onConfirmAction={() => {
            handleSaveNCallback(data, previewCallback)
          }}
          title="Save changes?"
          descriptionText={`Changes need to be saved before you can preview or share this hub.
          \n Once saved, the changes will be reflected on hubs created using this template.`}
        />),
      }))
    } else {
      handleSaveNCallback(data, previewCallback)
    }
    setTimeout(() => {
      setIsSaving(false)
    }, 1000)
  }, [rhForm, variant, isDirty, dirtyFields])

  const handleSaveWithDelay = (data: any) => {
    setIsSaving(true)
    // we check the readPermissions because we only show the modal if the template has been shared at least once
    const shouldShowModal = variant === HubVariant.TEMPLATE && 
      !!Object.keys(dirtyFields).length && isDirty && 
      (hubORM?.model.readPermissions ?? []).length > 0
    if (shouldShowModal) {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: false,
        component: () => (<DNACommonConfirmation
          cancelText="Cancel"
          confirmActionText="Save"
          onConfirmAction={() => {
            setTimeout(() => {
              handleSaveNCallback(data)
              setIsSaving(false)
            }, 500)
          }}
          title="Save changes?"
          descriptionText="Saved changes on locked sections will be immediately reflected on hubs created using this template."
        />),
      }))
      setIsSaving(false)
    } else {
      setTimeout(() => {
        handleSaveNCallback(data)
        setIsSaving(false)
      }, 500)
    }
  };

  const lockIcon = useMemo(() => {
    if (hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE) {
      // if templateId then show lock icon from template
      return (
        <CommonIcon variant={hubORM?.model.lockName ? 'lock-key' : 'lock-simple-open'} />
      )
    } else if (hubORM?.model.hubType === HubType.TEMPLATE) {
      return (
        <Controller
          name="lockName"
          rules={{ required: false }}
          control={control}
          render={({ field: { value, onChange } }) => (
            // we dont really need the disablePopover here since we are checking it above. But it is here for consistency
            <DNAPopover disablePopover={variant === HubVariant.STANDALONE}>
              <DNAPopover.Anchor>
                <DNAButton
                  appearance="ghost"
                  status="subtle"
                  iconLeft={value ? 'lock-key' : 'lock-simple-open'}
                  onPress={() => onChange(!value)}
                />
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText colorMode="dark">
                  This determines whether your audience can edit this section or not.
                </DNAText>
              </DNAPopover.Content>
            </DNAPopover>
          )}
          />
      )
    } else {
      return null
    }
  }, [hubORM?.model.lockName, hubORM?.model.hubType, control])

  return (
    <DNABox appearance="col">
      <DNABox childFill={1} style={ styles.hubTitleHeader }>
        {/* Tenant LOGO */}
        <TenantLogo />
        {/* Hub Title */}
        <DNABox fill childFill alignY="center" alignX="center" spacing="xs" style={styles.hubTitleContainer}>

            <DNABox fill childFill={1} alignY="center" alignX="center" spacing="sm">
            {/* Edit Hub Name Mode - Input & Buttons */}
            <DNABox
              fill
              alignY="center"
              alignX="center"
              spacing="sm"
              style={!editNameMode && { display: 'none' }}
            >
              {lockIcon}
              <Controller
                name="name"
                control={control}
                rules={{ required: 'NAME is required', maxLength: 150 }}
                render={() => (
                  <TextField.Kitten
                    testID="hub-header-text-input-box"
                    ref={nameRef}
                    style={ styles.editHubTitleInputBox }
                    value={hubName}
                    maxLength={150}
                    onChangeText={(text) => {
                      setHubName(text.trimStart())
                      rhForm.setValue(`name`, text.trimStart(), { shouldDirty: true })
                    }}
                    onKeyPress={(e) => {
                      if (e.nativeEvent.key === 'Enter') onSubmitHubName()
                    }}
                    accessoryRight={() => (
                      hubName.length > 0
                        ? <DNAButton
                            onPress={() => setHubName('')}
                            appearance="ghost"
                            status="subtle"
                            iconLeft="x-circle-fill"
                        /> : <></>
                    )}
                  />
                )}
              />
              <DNAButton
                testID="hub-title-cancel-btn"
                appearance="outline"
                status="tertiary"
                onPress={() => cancelHubNameChange()}
                children="Cancel"
              />
              <DNAButton
                testID="hub-title-done-btn"
                onPress={() => onSubmitHubName()}
                children="Done"
              />
            </DNABox>
            {/* Hub Name Read Only Mode - Text & Edit Button */}
            <Iffy is={!editNameMode}>
              <DNABox fill alignY="center" alignX="center">
                {lockIcon}
                <DNAText
                  testID='hub-header-text'
                  h4
                  numberOfLines={1}
                  style={{ paddingHorizontal: 8 }}
                >
                  {hubName}
                </DNAText>
                <Iffy is={hubORM?.model.hubType !== HubType.TEMPLATE_INSTANCE || (hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE && !hubORM.model.lockName)}>
                  <DNAButton
                    testID="hub-title-text-edit"
                    appearance="ghost"
                    status="subtle"
                    iconLeft="pencil-simple"
                    onPress={() => setEditNameMode(true)}
                  />
                </Iffy>
              </DNABox>
            </Iffy>
          </DNABox>
        </DNABox>
        {/* Action Buttons */}
        <Iffy is={!editNameMode}>
          <DNABox alignX="end" alignY="center" spacing="lg">
            <DNABox alignX="end" alignY="center" spacing="sm">
              {/* Because hub templates dont have delete, we are hiding the context menu here */}
              <Iffy is={hubORM?.model.hubType !== HubType.TEMPLATE}>
                <DNAContextMenu style={ styles.contextMenuItems }>
                  <DNAContextMenu.Anchor>
                    <DNAButton
                      appearance="outline"
                      status="tertiary"
                      iconRight="dots-three-vertical-bold"
                    />
                </DNAContextMenu.Anchor>
                <DNAContextMenu.Items>
                  <DNAContextMenu.Item
                    collapseOnPress
                    delay={100} // Workaround because sometimes the item deletes too fast before the ContextMenu can hide itself
                    title="Delete"
                    status="danger"
                    iconStatus="danger"
                    icon="trash"
                    onPress={() => handleDeleteHub(hubORM.model, true)}
                  />
                </DNAContextMenu.Items>
              </DNAContextMenu>
              </Iffy>
              <Iffy is={hubORM?.model.hubType === HubType.TEMPLATE}>
                <DNAButton
                  testID="edit-hub-share-btn"
                  appearance="outline"
                  status="tertiary"
                  onPress={handleSubmit(onSaveNShare, openFormInvalidWarningModal)}
                  disabled={isSaving}
                  children="Share"
                />
              </Iffy>
              <Iffy is={isOnline}>
                <DNAButton
                  testID="edit-hub-preview-btn"
                  appearance="outline"
                  status="tertiary"
                  onPress={handleSubmit(onSaveNPreview, openFormInvalidWarningModal)}
                  disabled={isSaving}
                  children="Preview"
                />
              </Iffy>
            </DNABox>
            <DNABox alignX="end" alignY="center" spacing="sm">
              <DNAButton
                testID="hub-cancel-edit-button"
                appearance="outline"
                status="tertiary"
                onPress={cancelEditHub}
                disabled={isSaving}
                children="Cancel"
              />
              <DNAButton
                testID='hub-save-edit-button'
                onPress={handleSubmit(handleSaveWithDelay, handleFormInvalid)}
                disabled={isSaving}
                children={isSaving ? 'Saving...' : 'Save'}
              />
            </DNABox>
          </DNABox>
        </Iffy>
      </DNABox>
      {/* Offline Banner */}
      <NetworkConnectionBanner />
      {/* Error Message */}
      <Iffy is={errorMessages.length}>
        <DNABox
          style={{
            padding: 15,
            backgroundColor: colors['color-danger-400'],
          }}
        >
          <DNAText colorMode="dark">
            {formattedFormErrors}
          </DNAText>
        </DNABox>
      </Iffy>
    </DNABox>
  )
}

export default Header
